import React, { FunctionComponent, memo, useCallback, useEffect, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useTranslation } from 'react-i18next'
import './e3dStream.css'
import Visualization from '../../../entities/Visualization'
import { IE3DRenderImage } from '../../../redux/selectors/e3dRenderImagesSelector'
import { normalizeConsumptionData } from '../../miwi-feature-app-loader/MiwiFeatureAppLoader'
import { logger } from '../../../utils/dccLogger'
import { createNotification } from '../../../redux/actions/app/notification.actions'
import { getE3DErrorTranslationKey, isKnownE3DErrorCode } from '../../../utils/e3dErrorHandler'
import { useE3DReinit } from '../e3d-context/E3DReinitContext'
import { isAuthenticated } from '../../../services/e3DAuthService'

const STREAM_START_DELAY_MS = 500
const SHOW_CAR_AFTER_SESSION_MS = 600
const SHOW_CAR_AFTER_RESUME_MS = 300
const RETRY_DELAY_MS = 2000
const RESUME_FALLBACK_MS = 500
const MAX_SESSION_ATTEMPTS = 3
const MODE_POLL_MS = 300

type Props = {
    visualization: Visualization
    consumptionData: string[]
    e3dRenderImages: IE3DRenderImage[]
    isE3DAvailable: boolean
    carlineName: string
    activeRenderImgIdx: number
    setIsE3DStreamActive?: (active: boolean) => void
    hideHangupButton: boolean
    forceWltpDataUpdate: boolean
}

const E3DStream: FunctionComponent<Props> = ({
    visualization,
    consumptionData,
    e3dRenderImages,
    isE3DAvailable,
    carlineName,
    activeRenderImgIdx,
    setIsE3DStreamActive,
    hideHangupButton,
    forceWltpDataUpdate,
}) => {
    const [isWaitingForResume, setIsWaitingForResume] = useState(() => {
        try {
            if (new URLSearchParams(window.location.search).has('e3d-sid')) { return true }
            if (sessionStorage.getItem('e3d_resume_pending')) { return true }
        } catch { /* no-op */ }
        return false
    })
    const { reinitCounter } = useE3DReinit()
    const dispatch = useDispatch()
    const { t } = useTranslation()
    const wltpText = normalizeConsumptionData(consumptionData).join(' ')

    const vizRef = useRef(visualization)
    const imagesRef = useRef(e3dRenderImages)
    const imgIdxRef = useRef(activeRenderImgIdx)
    const isAvailRef = useRef(isE3DAvailable)
    const waitingRef = useRef(isWaitingForResume)
    const streamActiveCbRef = useRef(setIsE3DStreamActive)

    useEffect(() => { vizRef.current = visualization }, [visualization])
    useEffect(() => { imagesRef.current = e3dRenderImages }, [e3dRenderImages])
    useEffect(() => { imgIdxRef.current = activeRenderImgIdx }, [activeRenderImgIdx])
    useEffect(() => { isAvailRef.current = isE3DAvailable }, [isE3DAvailable])
    useEffect(() => { waitingRef.current = isWaitingForResume }, [isWaitingForResume])
    useEffect(() => { streamActiveCbRef.current = setIsE3DStreamActive }, [setIsE3DStreamActive])

    const callShowCar = useCallback((prString: string) => {
        if (!window.E3D?.showCar || !prString) {
            logger.warn('callShowCar: early return', {
                hasE3D: Boolean(window.E3D),
                hasShowCar: Boolean(window.E3D?.showCar),
                hasPrString: Boolean(prString),
            })
            return
        }

        window.E3D.showCar(prString, carlineName, wltpText)

        if (forceWltpDataUpdate) {
            if (window.E3D?.vehicles?.updateVehicle && wltpText) {
                window.E3D.vehicles.updateVehicle({ wltpText })
            }
        }
    }, [carlineName, wltpText, forceWltpDataUpdate])

    const callShowImages = useCallback((images: IE3DRenderImage[], index: number = 0) => {
        if (!window.E3D?.showImages || !images || images.length === 0) {
            logger.warn('callShowImages: cannot show images', {
                hasE3D: Boolean(window.E3D),
                hasShowImages: Boolean(window.E3D?.showImages),
                hasImagesArray: Boolean(images),
                imagesLength: images?.length ?? 0,
            })
            return
        }

        if (window.E3D?.session) {
            window.E3D.session.setValues({ imageOverrideMode: 'manual' })
        }

        logger.info('callShowImages: Showing DCC images with override=true', {
            imageCount: images.length,
            activeIndex: index,
        })
        window.E3D.showImages(images, index, true)
    }, [])

    useEffect(() => {
        if (!window?.E3D?.session || !streamActiveCbRef.current) {
            return
        }

        const session = window.E3D.session

        const restartStream = (logMessage: string) => {
            const { current: viz } = vizRef
            const { current: images } = imagesRef
            const { current: idx } = imgIdxRef

            logger.log(logMessage)

            if (viz.aveString) {
                callShowCar(viz.aveString)
            }
            if (images && images.length > 0) {
                callShowImages(images, idx)
            }
        }

        const handleSessionEnd = () => restartStream('E3D session ended, calling showCar to restart stream')
        const handleDisconnected = () => restartStream('E3D disconnected, calling showCar to restart stream')

        const handleSharingStarted = () => {
            logger.log('E3D sharing started')
            streamActiveCbRef.current?.(true)
        }

        const handleStreamStart = () => {
            logger.log('E3D stream started')
            streamActiveCbRef.current?.(true)

            setTimeout(() => {
                const { current: viz } = vizRef
                const { current: images } = imagesRef
                const { current: idx } = imgIdxRef

                if (viz.aveString) {
                    logger.info('E3DStream handleStreamStart: calling showCar after stream start')
                    callShowCar(viz.aveString)
                }
                if (images && images.length > 0) {
                    callShowImages(images, idx)
                }
            }, STREAM_START_DELAY_MS)
        }

        const handleE3DError = (error: E3D.E3DError) => {
            logger.error('E3D error occurred:', error)
            const errorCode = typeof error.code === 'string' ? parseInt(error.code, 10) : error.code

            let errorMessage: string
            if (errorCode && !isNaN(errorCode) && isKnownE3DErrorCode(errorCode)) {
                errorMessage = t(getE3DErrorTranslationKey(errorCode))
            } else if (error.message) {
                errorMessage = error.message
            } else if (error.code) {
                errorMessage = t('e3d.error.withCode', { code: error.code })
            } else {
                errorMessage = t('e3d.error.generic')
            }

            dispatch(createNotification({ type: 'error', message: errorMessage }))
        }

        logger.info('E3DStream effect[event wiring] adding listeners')

        session.on('session:end', handleSessionEnd)
        if (window.E3D.on) {
            window.E3D.on('error', handleE3DError)
        }

        window.addEventListener('sharing-started', handleSharingStarted)
        window.addEventListener('disconnected', handleDisconnected)
        window.addEventListener('e3d-stream-end', handleSessionEnd)

        const streamElement = document.querySelector('e3d-stream')
        if (streamElement) {
            logger.info('E3DStream effect[event wiring] found e3d-stream element, wiring web component events')
            streamElement.addEventListener('e3d-stream-start', handleStreamStart)
            streamElement.addEventListener('e3d-stream-end', handleSessionEnd)
            streamElement.addEventListener('e3d-stream-error', () => {
                logger.error('E3D stream error from web component')
                const { current: viz } = vizRef
                if (viz.aveString) {
                    callShowCar(viz.aveString)
                }
            })
        } else {
            logger.warn('E3DStream effect[event wiring] no e3d-stream element found in DOM')
        }

        return () => {
            logger.info('E3DStream effect[event wiring] cleanup')

            if (session?.off) {
                session.off('session:end', handleSessionEnd)
            }
            if (window.E3D?.off) {
                window.E3D.off('error', handleE3DError)
            }

            window.removeEventListener('sharing-started', handleSharingStarted)
            window.removeEventListener('disconnected', handleDisconnected)
            window.removeEventListener('e3d-stream-end', handleSessionEnd)

            const el = document.querySelector('e3d-stream')
            if (el) {
                el.removeEventListener('e3d-stream-start', handleStreamStart)
                el.removeEventListener('e3d-stream-end', handleSessionEnd)
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    useEffect(() => {
        if (!isAvailRef.current || waitingRef.current) {
            return
        }
        if (!window?.E3D?.session?.get) {
            return
        }

        let lastExperience = ''
        let pollCount = 0
        const maxPolls = 50 // ~15 seconds

        const tick = () => {
            try {
                const session = window.E3D.session.get()
                const currentExperience = session?.activeExperience || ''

                if (currentExperience !== lastExperience) {
                    logger.info('E3DStream mode poll detected experience change', {
                        from: lastExperience,
                        to: currentExperience,
                        imageOverrideMode: session?.imageOverrideMode,
                        carImagesStatus: session?.carImagesStatus,
                    })
                    lastExperience = currentExperience

                    if (currentExperience === '2D') {
                        const { current: images } = imagesRef
                        const { current: idx } = imgIdxRef
                        if (images && images.length > 0 && session?.imageOverrideMode === 'manual') {
                            logger.info('E3DStream mode poll: calling showImages for 2D mode')
                            callShowImages(images, idx)
                        }
                    }
                }
            } catch (e) {
                // session.get may throw if SDK not ready
            }

            pollCount += 1
            if (pollCount < maxPolls) {
                // keep polling for transitions
            }
        }

        tick() // initial check
        const interval = setInterval(tick, MODE_POLL_MS)

        return () => {
            clearInterval(interval)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    useEffect(() => {
        if (waitingRef.current) {
            logger.info('E3DStream effect[reinit showCar] skipping — waiting for e3d-sid session resume')
            return
        }
        if (!window?.E3D?.session || !isAvailRef.current || !visualization.aveString) {
            return
        }

        logger.info('E3DStream effect[reinit showCar] start', {
            reinitCounter,
            hasSession: Boolean(window?.E3D?.session),
            isE3DAvailable: isAvailRef.current,
            hasAveString: Boolean(visualization.aveString),
        })

        callShowCar(visualization.aveString)
    }, [reinitCounter, visualization.aveString, callShowCar])

    useEffect(() => {
        if (waitingRef.current) {
            logger.info('E3DStream effect[showImages] skipping — waiting for e3d-sid session resume')
            return
        }
        if (!window?.E3D?.showImages || !isE3DAvailable || !e3dRenderImages || e3dRenderImages.length === 0) {
            return
        }

        callShowImages(e3dRenderImages, activeRenderImgIdx)
    }, [e3dRenderImages, isE3DAvailable, reinitCounter, activeRenderImgIdx, callShowImages])

    useEffect(() => {
        const hadE3dSid = isWaitingForResume
        let hasStarted = false
        let attempts = 0
        let sessionFallbackTimer: ReturnType<typeof setTimeout> | null = null

        const showCarAndImages = (logLabel = '') => {
            const { current: viz } = vizRef
            const { current: images } = imagesRef
            const { current: idx } = imgIdxRef

            if (logLabel) {
                logger.info(logLabel)
            }
            if (viz.aveString) {
                callShowCar(viz.aveString)
            }
            if (images && images.length > 0) {
                callShowImages(images, idx)
            }
        }

        const maybeStartSession = (reason: string) => {
            if (!isAuthenticated()) {
                logger.warn('E3DStream effect[startSession] token expired or missing, skipping startSession')
                return
            }

            logger.info('E3DStream effect[startSession] maybeStartSession invoked', {
                reason,
                hasStarted,
                attempts,
                maxAttempts: MAX_SESSION_ATTEMPTS,
                hasStartSession: Boolean(window?.E3D?.startSession),
            })

            if (hasStarted || !window?.E3D?.startSession || attempts >= MAX_SESSION_ATTEMPTS) {
                logger.warn('E3DStream effect[startSession] not starting session', {
                    hasStarted,
                    attempts,
                    maxAttempts: MAX_SESSION_ATTEMPTS,
                    hasStartSession: Boolean(window?.E3D?.startSession),
                })
                return
            }

            attempts += 1
            hasStarted = true

            try {
                const promise = window.E3D.startSession()
                logger.info('E3DStream effect[startSession] called E3D.startSession()', { reason, attempts })

                promise
                    .then(() => {
                        logger.info('E3DStream effect[startSession] session created')
                        setTimeout(() => {
                            showCarAndImages('E3DStream effect[startSession] showing car after session created')
                        }, SHOW_CAR_AFTER_SESSION_MS)
                    })
                    .catch((e: Error) => {
                        logger.error('E3DStream effect[startSession] Error starting share E3D', {
                            message: e?.message,
                            attempts,
                        })

                        if (attempts < MAX_SESSION_ATTEMPTS) {
                            hasStarted = false
                            logger.warn('E3DStream effect[startSession] scheduling retry', {
                                nextAttempt: attempts + 1,
                                retryDelayMs: RETRY_DELAY_MS,
                            })
                            setTimeout(() => {
                                maybeStartSession('retry-after-error')
                            }, RETRY_DELAY_MS)
                        }
                    })
            } catch (e: any) {
                logger.error('E3DStream effect[startSession] synchronous error calling E3D.startSession', {
                    message: e?.message,
                    attempts,
                })
            }
        }

        if (hadE3dSid) {
            logger.info('E3DStream effect[startSession] e3d-sid detected, waiting for session resume')
            sessionFallbackTimer = setTimeout(() => {
                try { sessionStorage.removeItem('e3d_resume_pending') } catch { /* no-op */ }
                if (!window?.E3D?.session) {
                    logger.warn('E3DStream effect[startSession] no session after e3d-sid wait, falling back to startSession')
                    setIsWaitingForResume(false)
                    maybeStartSession('e3d-sid-fallback')
                } else {
                    logger.info('E3DStream effect[startSession] session exists after e3d-sid wait, resume OK')
                    setIsWaitingForResume(false)

                    setTimeout(() => {
                        showCarAndImages('E3DStream effect[startSession] post-resume showCar')
                    }, SHOW_CAR_AFTER_RESUME_MS)
                }
            }, RESUME_FALLBACK_MS)
        } else if (window?.E3D?.startSession) {
            maybeStartSession('immediate-availability')
        }

        const runtime = window?.E3D as
            | { on?: (event: string, handler: () => void) => void; off?: (event: string, handler: () => void) => void }
            | undefined
        const hasEventApi = runtime && typeof runtime.on === 'function'

        const runtimeInitializedHandler = () => {
            if (!hadE3dSid) {
                maybeStartSession('runtime-initialized')
            }
        }

        if (hasEventApi) {
            logger.info('E3DStream effect[startSession] subscribing to runtime initialized event')
            runtime!.on('initialized', runtimeInitializedHandler)
        } else {
            logger.warn('E3DStream effect[startSession] runtime event API not available on window.E3D')
        }

        const domInitializedHandler = () => {
            if (!hadE3dSid) {
                maybeStartSession('dom-e3d-runtime-initialized')
            }
        }
        window.addEventListener('e3d-runtime-initialized', domInitializedHandler)

        return () => {
            logger.info('E3DStream effect[startSession] cleanup')

            if (sessionFallbackTimer) {
                clearTimeout(sessionFallbackTimer)
            }

            if (hasEventApi && runtime && typeof runtime.off === 'function') {
                runtime.off('initialized', runtimeInitializedHandler)
            }
            window.removeEventListener('e3d-runtime-initialized', domInitializedHandler)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    return <e3d-stream data-hide-hangup-button={hideHangupButton ? 'true' : undefined}></e3d-stream>
}

export default memo(E3DStream)
